import type {
  PhaseConformityRecoveryAction,
  PhaseConformitySummary,
} from '@internal/spassu-code-contract';

const REASON_LABELS: Record<string, string> = {
  SKILL_REQUIRED_UNAVAILABLE: 'skill obrigatória indisponível',
  SKILL_INVOCATION_NOT_OBSERVED: 'uso da skill não confirmado',
  SKILL_INVOCATION_MISMATCH: 'skill usada não corresponde à fase',
  APPROACH_NOT_PRESENTED: 'abordagens ainda não apresentadas',
  APPROACH_NOT_SELECTED: 'abordagem ainda não selecionada',
  ARTIFACT_REQUIRED_MISSING: 'arquivo obrigatório ausente',
  ARTIFACT_PATH_INVALID: 'arquivo fora do local esperado',
  ARTIFACT_TOO_LARGE: 'arquivo acima do limite permitido',
  ARTIFACT_ANCHORS_MISSING: 'seções essenciais ausentes',
  TASK_PLAN_MISSING: 'plano de tarefas ausente',
  QA_REPORT_MISSING: 'relatório de qualidade incompleto',
  PR_NOT_PERSISTED: 'Pull Request ainda não registrado',
  PR_INCONSISTENT: 'Pull Request registrado não pertence a este repositório',
  WORKER_PHASE_CONFORMITY_UNSUPPORTED: 'Worker precisa ser atualizado',
};

export interface PhaseConformityRecoveryView {
  severity: 'info' | 'warning';
  message: string;
  details: string;
  action?: {
    code: PhaseConformityRecoveryAction;
    label: string;
    pendingLabel: string;
    errorPrefix: string;
  };
}

const RECOVERY_ACTIONS: Record<
  PhaseConformityRecoveryAction,
  NonNullable<PhaseConformityRecoveryView['action']>
> = {
  revalidate_artifact: {
    code: 'revalidate_artifact',
    label: 'Revalidar arquivo',
    pendingLabel: 'Revalidando...',
    errorPrefix: 'Não foi possível revalidar o arquivo',
  },
  rerun_phase: {
    code: 'rerun_phase',
    label: 'Reexecutar QA',
    pendingLabel: 'Reexecutando...',
    errorPrefix: 'Não foi possível reexecutar o QA',
  },
  revalidate_delivery: {
    code: 'revalidate_delivery',
    label: 'Revalidar entrega',
    pendingLabel: 'Revalidando...',
    errorPrefix: 'Não foi possível revalidar a entrega',
  },
  reset_pr: {
    code: 'reset_pr',
    label: 'Refazer PR',
    pendingLabel: 'Refazendo...',
    errorPrefix: 'Não foi possível refazer o Pull Request',
  },
  replan_tasks: {
    code: 'replan_tasks',
    label: 'Replanejar tasks',
    pendingLabel: 'Replanejando...',
    errorPrefix: 'Não foi possível replanejar as tasks',
  },
};

export function getPhaseConformityRecoveryView(
  summary: PhaseConformitySummary | null | undefined,
): PhaseConformityRecoveryView | null {
  if (!summary || !['repair_required', 'blocked'].includes(summary.status)) {
    return null;
  }
  const details = summary.missingReasonCodes
    .map(code => REASON_LABELS[code] ?? 'critério obrigatório pendente')
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .join('; ');
  return {
    severity: summary.status === 'blocked' ? 'warning' : 'info',
    message:
      summary.status === 'blocked'
        ? 'A correção automática terminou sem atender todos os critérios.'
        : 'A fase ainda precisa atender critérios de conformidade.',
    details,
    ...(summary.recoveryAction
      ? { action: RECOVERY_ACTIONS[summary.recoveryAction] }
      : {}),
  };
}
